// <copyright company="Microsoft">Copyright (c) Microsoft. All rights reserved.</copyright>

import Configuration from '../../config/einvoicing-configuration';
import { clearReauthFlag, recoverFromUnauthorized } from '../../utils/authToken';

export interface RelatedCfdi {
    uuid: string;
    canLookup: boolean;
}

export interface SupplierResponseDiagnostics {
    httpStatusCode: number | null;
    redactedBody: string | null;
    validationStage: string;
}

export interface CfdiLookupResult {
    uuid: string;
    issuerRfc: string;
    retrievedAtUtc: string;
    status: {
        documentStatus: string;
        cancellability: string | null;
        cancellationStatus: string | null;
    } | null;
    statusError: string | null;
    relationships: {
        success: boolean;
        code: string;
        parents: RelatedCfdi[];
        children: RelatedCfdi[];
    } | null;
    relationshipsError: string | null;
    diagnostics?: {
        status: SupplierResponseDiagnostics;
        relationships: SupplierResponseDiagnostics;
    } | null;
}

export const SUPPORTED_ISSUER_RFC = 'MME910620Q85';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isCfdiUuid(value: string): boolean {
    return UUID_PATTERN.test(value) && value !== '00000000-0000-0000-0000-000000000000';
}

function isRelatedCfdi(document: RelatedCfdi): boolean {
    return !!document && typeof document.uuid === 'string' && isCfdiUuid(document.uuid) &&
        typeof document.canLookup === 'boolean';
}

function isResponseDiagnostics(value: SupplierResponseDiagnostics): boolean {
    return !!value && typeof value === 'object' &&
        (value.httpStatusCode === null || (Number.isInteger(value.httpStatusCode) && value.httpStatusCode >= 100 && value.httpStatusCode <= 599)) &&
        (value.redactedBody === null || (typeof value.redactedBody === 'string' && value.redactedBody.length <= 65536)) &&
        typeof value.validationStage === 'string' && /^[a-z-]{1,40}$/.test(value.validationStage);
}

export async function lookupCfdi(uuid: string, token: string, signal: AbortSignal, includeDiagnostics = false): Promise<CfdiLookupResult> {
    const query = includeDiagnostics ? '?includeDiagnostics=true' : '';
    const response = await fetch(`${Configuration.eventServiceUrl}/dashboard/cfdi/${encodeURIComponent(uuid)}${query}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
        signal,
    });
    if (!response.ok && response.status !== 502) {
        if (response.status === 401) {
            recoverFromUnauthorized();
            throw new Error('Your sign-in is no longer valid. Sign in again to consult this CFDI.');
        }
        if (response.status === 403) {
            throw new Error('Mexico, production mode and All Partners read access are required for CFDI lookup.');
        }
        if (response.status === 429) {
            throw new Error('CFDI lookup is busy or the six-lookups-per-minute limit was reached. Wait a minute before retrying.');
        }
        throw new Error(`CFDI lookup is unavailable (HTTP ${response.status}). Retry later or contact e-Invoicing support.`);
    }

    const result: CfdiLookupResult = await response.json();
    if (!result || result.uuid?.toLowerCase() !== uuid.toLowerCase() || result.issuerRfc !== SUPPORTED_ISSUER_RFC ||
        !Number.isFinite(Date.parse(result.retrievedAtUtc)) ||
        (!result.status && !result.statusError) || (!result.relationships && !result.relationshipsError) ||
        (result.status !== null && (typeof result.status !== 'object' ||
            typeof result.status.documentStatus !== 'string' || !result.status.documentStatus.trim() ||
            (result.status.cancellability !== null && typeof result.status.cancellability !== 'string') ||
            (result.status.cancellationStatus !== null && typeof result.status.cancellationStatus !== 'string'))) ||
        (response.status === 502 && (result.status || result.relationships)) ||
        (result.diagnostics != null && (!isResponseDiagnostics(result.diagnostics.status) ||
            !isResponseDiagnostics(result.diagnostics.relationships))) ||
        (result.relationships && (result.relationships.success !== true ||
            !Array.isArray(result.relationships.parents) || !Array.isArray(result.relationships.children) ||
            !result.relationships.parents.every(isRelatedCfdi) || !result.relationships.children.every(isRelatedCfdi)))) {
        throw new Error('The lookup service returned an invalid result. Contact e-Invoicing support.');
    }
    clearReauthFlag();
    return result;
}
